import React, { useState } from 'react';
import Button, { IconButton } from '../components/Button';
import {
  SkeletonLoader,
  MemberSkeleton,
  StatCardSkeleton,
  TableSkeleton,
  DashboardWidgetSkeleton
} from '../components/SkeletonLoader';
import {
  PlusIcon,
  CheckIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  SparklesIcon,
  HeartIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import MemberAvatar from '../components/MemberAvatar';
import { getRoleIconComponent } from '../utils/roleIcons';
import AttributeLabel from '../components/AttributeLabel';
import {
  attributeIconMap,
  ATTRIBUTE_GROUP_ORDER,
  ATTRIBUTE_ICON_KEYS,
  type AttributeIconKey,
} from '../utils/attributeIcons';
import SideDogIcon from '../components/icons/SideDogIcon';
import SyringeIcon from '../components/icons/SyringeIcon';
import GenderIcon from '../components/icons/GenderIcon';
import {
  ModalShell,
  ModalActions,
  ModalSection,
  ModalRow,
} from '../components/modals/core';
import { UserIcon, DocumentTextIcon, TrashIcon as TrashOutlineIcon } from '@heroicons/react/24/outline';

// Demo-Rollen analog zur DB (roles-Tabelle inkl. icon-Spalte)
const DEMO_ROLES = {
  board: { id: 5, name: 'board', description: 'Vorstand', priority: 5, icon: 'shield-check' },
  auditor: { id: 4, name: 'auditor', description: 'Kassenprüfer', priority: 4, icon: 'check-badge' },
  trainer: { id: 3, name: 'trainer', description: 'Trainer', priority: 3, icon: 'trophy' },
  member: { id: 2, name: 'member', description: 'Vollmitglied', priority: 2, icon: 'user' },
  guest: { id: 1, name: 'guest', description: 'Gastmitglied', priority: 1, icon: 'user-plus' },
};

const DEMO_AVATAR_MEMBERS = [
  { label: 'Vorstand', member: { vorname: 'Vera', name: 'Xander', active: 1, roles: [DEMO_ROLES.board, DEMO_ROLES.member] } },
  { label: 'Trainer', member: { vorname: 'Tim', name: 'Xaver', active: 1, roles: [DEMO_ROLES.trainer, DEMO_ROLES.member] } },
  { label: 'Kassenprüfer', member: { vorname: 'Kim', name: 'Xu', active: 1, roles: [DEMO_ROLES.auditor, DEMO_ROLES.member] } },
  { label: 'Mitglied', member: { vorname: 'Mia', name: 'Xylander', active: 1, roles: [DEMO_ROLES.member] } },
  { label: 'Gast', member: { vorname: 'Gerd', name: 'Xanthos', active: 1, roles: [DEMO_ROLES.guest] } },
  { label: 'Inaktiv', member: { vorname: 'Ida', name: 'Xberg', active: 0, roles: [DEMO_ROLES.member] } },
];

const ROLE_BADGE_CLASSES: Record<string, string> = {
  board: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300',
  trainer: 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-300',
  auditor: 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300',
  member: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300',
  guest: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

const ComponentsDemo: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showSkeletons, setShowSkeletons] = useState(true);
  const [demoModal, setDemoModal] = useState<
    'confirm' | 'form' | 'detail' | 'choice' | 'viewer' | null
  >(null);

  const handleLoadingDemo = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 2000);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <header className="app-page-header bg-white p-4 dark:bg-gray-800 sm:p-6">
        <div className="flex items-center space-x-3">
          <SparklesIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Design System Demo</h1>
            <p className="text-gray-500 dark:text-gray-400">Neue Komponenten und Verbesserungen</p>
          </div>
        </div>
      </header>

      {/* Button Hierarchie */}
      <section className="bg-gray-800 rounded-lg p-6 space-y-4 border border-gray-700">
        <h2 className="text-xl font-bold text-white flex items-center">
          <span className="bg-primary-600 text-white px-2 py-1 rounded text-sm mr-2">NEU</span>
          Button-Hierarchie
        </h2>
        <p className="text-gray-400 text-sm">
          Klare Unterscheidung zwischen Haupt-, Neben- und optionalen Aktionen
        </p>

        <div className="space-y-6">
          {/* Primary Buttons */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Primary - Hauptaktionen
            </h3>
            <div className="flex flex-wrap gap-3">
              <Button variant="primary">Primary Button</Button>
              <Button variant="primary" size="sm">Small</Button>
              <Button variant="primary" size="lg">Large</Button>
              <Button variant="primary" loading={loading} onClick={handleLoadingDemo}>
                {loading ? 'Wird geladen...' : 'Mit Loading'}
              </Button>
              <Button variant="primary" icon={<PlusIcon className="h-5 w-5" />}>
                Mit Icon
              </Button>
              <Button variant="primary" icon={<CheckIcon className="h-5 w-5" />} iconPosition="right">
                Icon Rechts
              </Button>
            </div>
          </div>

          {/* Secondary Buttons */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Secondary - Nebenaktionen
            </h3>
            <div className="flex flex-wrap gap-3">
              <Button variant="secondary">Secondary Button</Button>
              <Button variant="secondary" size="sm">Small</Button>
              <Button variant="secondary" size="lg">Large</Button>
              <Button variant="secondary" icon={<InformationCircleIcon className="h-5 w-5" />}>
                Mit Icon
              </Button>
            </div>
          </div>

          {/* Tertiary Buttons */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Tertiary - Optionale Aktionen
            </h3>
            <div className="flex flex-wrap gap-3">
              <Button variant="tertiary">Tertiary Button</Button>
              <Button variant="tertiary" size="sm">Small</Button>
              <Button variant="tertiary" size="lg">Large</Button>
              <Button variant="tertiary" icon={<StarIcon className="h-5 w-5" />}>
                Mit Icon
              </Button>
            </div>
          </div>

          {/* Feedback Buttons */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Feedback - Success / Warning / Error
            </h3>
            <div className="flex flex-wrap gap-3">
              <Button variant="success" icon={<CheckIcon className="h-5 w-5" />}>
                Erfolg
              </Button>
              <Button variant="warning" icon={<ExclamationTriangleIcon className="h-5 w-5" />}>
                Warnung
              </Button>
              <Button variant="error" icon={<TrashIcon className="h-5 w-5" />}>
                Löschen
              </Button>
            </div>
          </div>

          {/* Icon Buttons */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Icon Buttons
            </h3>
            <div className="flex flex-wrap gap-3">
              <IconButton
                variant="primary"
                icon={<PlusIcon className="h-5 w-5" />}
                ariaLabel="Hinzufügen"
              />
              <IconButton
                variant="secondary"
                icon={<CheckIcon className="h-5 w-5" />}
                ariaLabel="Bestätigen"
              />
              <IconButton
                variant="tertiary"
                icon={<HeartIcon className="h-5 w-5" />}
                ariaLabel="Favorit"
              />
              <IconButton
                variant="error"
                icon={<TrashIcon className="h-5 w-5" />}
                ariaLabel="Löschen"
                size="sm"
              />
              <IconButton
                variant="success"
                icon={<CheckIcon className="h-5 w-5" />}
                ariaLabel="Erfolg"
                size="lg"
              />
            </div>
          </div>

          {/* Disabled State */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Disabled State
            </h3>
            <div className="flex flex-wrap gap-3">
              <Button variant="primary" disabled>Deaktiviert</Button>
              <Button variant="secondary" disabled>Deaktiviert</Button>
              <Button variant="tertiary" disabled>Deaktiviert</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Farbsystem */}
      <section className="bg-gray-800 rounded-lg p-6 space-y-4 border border-gray-700">
        <h2 className="text-xl font-bold text-white flex items-center">
          <span className="bg-primary-600 text-white px-2 py-1 rounded text-sm mr-2">NEU</span>
          Einheitliches Farbsystem
        </h2>
        <p className="text-gray-400 text-sm">
          Konsistente Farben für Feedback und Status-Anzeigen
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Success */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-400 uppercase">Success</h3>
            <div className="space-y-1">
              <div className="h-10 bg-success-600 rounded flex items-center justify-center text-white text-sm font-medium">
                success-600
              </div>
              <div className="h-8 bg-success-500 rounded flex items-center justify-center text-white text-xs">
                success-500
              </div>
              <div className="h-8 bg-success-700 rounded flex items-center justify-center text-white text-xs">
                success-700
              </div>
            </div>
            <div className="p-3 bg-success-600 text-white rounded-lg text-sm">
              ✓ Erfolgreich gespeichert!
            </div>
          </div>

          {/* Warning */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-400 uppercase">Warning</h3>
            <div className="space-y-1">
              <div className="h-10 bg-warning-600 rounded flex items-center justify-center text-white text-sm font-medium">
                warning-600
              </div>
              <div className="h-8 bg-warning-500 rounded flex items-center justify-center text-white text-xs">
                warning-500
              </div>
              <div className="h-8 bg-warning-700 rounded flex items-center justify-center text-white text-xs">
                warning-700
              </div>
            </div>
            <div className="p-3 bg-warning-600 text-white rounded-lg text-sm">
              ⚠ Bitte überprüfen Sie Ihre Eingabe
            </div>
          </div>

          {/* Error */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-400 uppercase">Error</h3>
            <div className="space-y-1">
              <div className="h-10 bg-error-600 rounded flex items-center justify-center text-white text-sm font-medium">
                error-600
              </div>
              <div className="h-8 bg-error-500 rounded flex items-center justify-center text-white text-xs">
                error-500
              </div>
              <div className="h-8 bg-error-700 rounded flex items-center justify-center text-white text-xs">
                error-700
              </div>
            </div>
            <div className="p-3 bg-error-600 text-white rounded-lg text-sm">
              ✗ Ein Fehler ist aufgetreten
            </div>
          </div>

          {/* Info */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-400 uppercase">Info</h3>
            <div className="space-y-1">
              <div className="h-10 bg-info-600 rounded flex items-center justify-center text-white text-sm font-medium">
                info-600
              </div>
              <div className="h-8 bg-info-500 rounded flex items-center justify-center text-white text-xs">
                info-500
              </div>
              <div className="h-8 bg-info-700 rounded flex items-center justify-center text-white text-xs">
                info-700
              </div>
            </div>
            <div className="p-3 bg-info-600 text-white rounded-lg text-sm">
              ℹ Wichtige Information
            </div>
          </div>
        </div>
      </section>

      {/* Skeleton Loader */}
      <section className="bg-gray-800 rounded-lg p-6 space-y-4 border border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center">
              <span className="bg-primary-600 text-white px-2 py-1 rounded text-sm mr-2">NEU</span>
              Skeleton Loader
            </h2>
            <p className="text-gray-400 text-sm">
              Statt "Lade..." Text für bessere User Experience
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowSkeletons(!showSkeletons)}
          >
            {showSkeletons ? 'Geladene Ansicht' : 'Skeleton Ansicht'}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Basic Skeleton */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Basic Skeleton Loader
            </h3>
            <div className="space-y-2">
              <SkeletonLoader variant="text" width="100%" height={20} />
              <SkeletonLoader variant="text" width="80%" height={20} />
              <SkeletonLoader variant="text" width="60%" height={20} />
            </div>
          </div>

          {/* Stat Cards */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Stat Cards Skeleton
            </h3>
            {showSkeletons ? (
              <StatCardSkeleton count={4} />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-700 rounded-lg p-6">
                  <div className="text-sm text-gray-400">Mitglieder</div>
                  <div className="text-3xl font-bold text-white">38</div>
                  <div className="text-xs text-gray-500">+2 diesen Monat</div>
                </div>
                <div className="bg-gray-700 rounded-lg p-6">
                  <div className="text-sm text-gray-400">Gäste</div>
                  <div className="text-3xl font-bold text-white">20</div>
                  <div className="text-xs text-gray-500">5 aktiv</div>
                </div>
                <div className="bg-gray-700 rounded-lg p-6">
                  <div className="text-sm text-gray-400">Aktive Hunde</div>
                  <div className="text-3xl font-bold text-white">89</div>
                  <div className="text-xs text-gray-500">99% komplett</div>
                </div>
                <div className="bg-gray-700 rounded-lg p-6">
                  <div className="text-sm text-gray-400">Verzehrkosten</div>
                  <div className="text-3xl font-bold text-white">€57</div>
                  <div className="text-xs text-gray-500">Unbezahlt</div>
                </div>
              </div>
            )}
          </div>

          {/* Member List */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Member List Skeleton
            </h3>
            {showSkeletons ? (
              <MemberSkeleton count={3} />
            ) : (
              <div className="space-y-3">
                <div className="flex items-center space-x-4 p-4 bg-gray-700 rounded-lg">
                  <div className="h-12 w-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold">
                    MT
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-white">Markus Tietke</div>
                    <div className="text-sm text-gray-400">Vorstand, Mitglied</div>
                  </div>
                  <Button variant="tertiary" size="sm">Details</Button>
                </div>
                <div className="flex items-center space-x-4 p-4 bg-gray-700 rounded-lg">
                  <div className="h-12 w-12 bg-info-600 rounded-full flex items-center justify-center text-white font-bold">
                    LB
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-white">Lara Bergmann</div>
                    <div className="text-sm text-gray-400">Mitglied</div>
                  </div>
                  <Button variant="tertiary" size="sm">Details</Button>
                </div>
              </div>
            )}
          </div>

          {/* Table Skeleton */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Table Skeleton
            </h3>
            {showSkeletons ? (
              <TableSkeleton rows={4} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Rolle</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    <tr className="bg-gray-800">
                      <td className="px-4 py-3 text-white">Markus Tietke</td>
                      <td className="px-4 py-3"><span className="px-2 py-1 bg-success-600 text-white text-xs rounded">Aktiv</span></td>
                      <td className="px-4 py-3 text-gray-400">Vorstand</td>
                      <td className="px-4 py-3"><Button variant="tertiary" size="sm">Bearbeiten</Button></td>
                    </tr>
                    <tr className="bg-gray-800">
                      <td className="px-4 py-3 text-white">Lara Bergmann</td>
                      <td className="px-4 py-3"><span className="px-2 py-1 bg-success-600 text-white text-xs rounded">Aktiv</span></td>
                      <td className="px-4 py-3 text-gray-400">Mitglied</td>
                      <td className="px-4 py-3"><Button variant="tertiary" size="sm">Bearbeiten</Button></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Widget Skeleton */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Dashboard Widget Skeleton
            </h3>
            {showSkeletons ? (
              <DashboardWidgetSkeleton />
            ) : (
              <div className="bg-gray-700 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="h-10 w-10 bg-primary-600 rounded-full flex items-center justify-center">
                    <SparklesIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-white">12</div>
                </div>
                <div className="text-white font-semibold">Aktive Trainings</div>
                <div className="text-sm text-gray-400">3 heute geplant</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Avatare & Rollen */}
      <section className="bg-gray-800 rounded-lg p-6 space-y-4 border border-gray-700">
        <h2 className="text-xl font-bold text-white flex items-center">
          <span className="bg-primary-600 text-white px-2 py-1 rounded text-sm mr-2">NEU</span>
          Avatare &amp; Rollen
        </h2>
        <p className="text-gray-400 text-sm">
          Einheitliche Avatar-Darstellung über <code className="text-primary-300">MemberAvatar</code>:
          Initialen auf Pastell-Hintergrund (oder Foto) mit Rollen-Badge unten rechts.
          Die Rollen-Icons kommen aus der Datenbank (<code className="text-primary-300">roles.icon</code>)
          und werden in der Rollenverwaltung gepflegt.
        </p>

        <div className="space-y-6">
          {/* Avatar mit Rolle */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              Avatar mit Rolle
            </h3>
            <div className="flex flex-wrap gap-6">
              {DEMO_AVATAR_MEMBERS.map(({ label, member }) => (
                <div key={label} className="flex flex-col items-center gap-2">
                  <MemberAvatar member={member} size="lg" />
                  <span className="text-xs text-gray-400">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RoleBadge */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
              RoleBadge
            </h3>
            <div className="flex flex-wrap gap-3">
              {Object.values(DEMO_ROLES).map((role) => {
                const IconComponent = getRoleIconComponent(role);
                return (
                  <span
                    key={role.name}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full ${ROLE_BADGE_CLASSES[role.name] || 'bg-gray-100 text-gray-800'}`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {role.description}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Code-Beispiel */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wider">
              Verwendung
            </h3>
            <pre className="bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm">
              <code className="text-gray-300">{`// Avatar mit Rollen-Badge (Rollen inkl. icon aus der API, z.B. /api/members)
<MemberAvatar
  member={member}                       // { vorname, name, active, roles }
  photoUrl={profileImages[member.mg_nr]} // optional: Foto
  hasImageError={imageErrors[member.mg_nr]}
  onImageError={() => setImageErrors(prev => ({ ...prev, [member.mg_nr]: true }))}
  className="h-12 w-12"                 // Größe anpassbar
/>

// Rollen-Icon einzeln (Fallback über Rollen-Namen)
const Icon = getRoleIconComponent(role); // role: { name, icon }
<Icon className="w-4 h-4" />`}</code>
            </pre>
          </div>
        </div>
      </section>

      {/* Attribut-Labels & Icons */}
      <section className="bg-gray-800 rounded-lg p-6 space-y-4 border border-gray-700">
        <h2 className="text-xl font-bold text-white flex items-center">
          <span className="bg-primary-600 text-white px-2 py-1 rounded text-sm mr-2">NEU</span>
          Attribut-Labels &amp; Icons
        </h2>
        <p className="text-gray-400 text-sm">
          Zentrale, seitenübergreifende Icon-Zuordnung für Feld- und Attributlabels
          (Issue #9). Gleicher fachlicher Begriff = gleiches Icon. Komponente{' '}
          <code className="text-primary-300">&lt;AttributeLabel iconKey="email"&gt;E-Mail&lt;/AttributeLabel&gt;</code>,
          zentrale Zuordnung in <code className="text-primary-300">src/utils/attributeIcons.ts</code>.
        </p>

        {/* Varianten */}
        <div>
          <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
            Varianten
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 space-y-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Standard (md, neutral)</p>
              <AttributeLabel iconKey="email" textClassName="text-gray-200 text-sm">E-Mail</AttributeLabel>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 space-y-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Kompakt (sm) – mobile Karten</p>
              <AttributeLabel iconKey="email" size="sm" textClassName="text-gray-300 text-xs">E-Mail</AttributeLabel>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 space-y-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Aktiv/positiv (primary)</p>
              <AttributeLabel iconKey="active" tone="primary" textClassName="text-gray-200 text-sm">Aktiv</AttributeLabel>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 space-y-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Als Formular-Label</p>
              <AttributeLabel iconKey="email" htmlFor="demo-email" textClassName="text-gray-200 text-sm font-medium">E-Mail</AttributeLabel>
              <input
                id="demo-email"
                type="email"
                placeholder="name@verein.de"
                className="w-full mt-1 px-3 py-1.5 rounded-md bg-gray-800 border border-gray-600 text-gray-100 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Sonder-Icons / Ausnahmen */}
        <div>
          <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
            Verbindliche Ausnahmen (zentral gekapselt)
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 flex items-center gap-3">
              <SideDogIcon className="h-7 w-7 text-primary-400" />
              <div>
                <p className="text-sm font-medium text-gray-200">Hund → SideDogIcon</p>
                <p className="text-xs text-gray-500">gefüllt · niemals HeartIcon</p>
              </div>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 flex items-center gap-3">
              <SyringeIcon className="h-7 w-7 text-gray-300" />
              <div>
                <p className="text-sm font-medium text-gray-200">Tollwutimpfung → Syringe</p>
                <p className="text-xs text-gray-500">Lucide-Style · Outline</p>
              </div>
            </div>
            <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 flex items-center gap-3">
              <GenderIcon className="h-7 w-7 text-primary-400" />
              <div>
                <p className="text-sm font-medium text-gray-200">Geschlecht → GenderIcon</p>
                <p className="text-xs text-gray-500">einheitlich · Vereinsgrün</p>
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Haftpflichtversicherung nutzt <code className="text-primary-300">ShieldCheckIcon</code> (Entscheidung zu Issue #9).
          </p>
        </div>

        {/* Komplette Matrix nach Gruppen */}
        <div>
          <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase tracking-wider">
            Komplette Zuordnung ({ATTRIBUTE_ICON_KEYS.length} Schlüssel)
          </h3>
          <div className="space-y-6">
            {ATTRIBUTE_GROUP_ORDER.map((group) => {
              const keys = ATTRIBUTE_ICON_KEYS.filter(
                (key) => attributeIconMap[key].group === group,
              );
              if (keys.length === 0) return null;
              return (
                <div key={group}>
                  <h4 className="text-xs font-semibold text-primary-300 mb-2 uppercase tracking-wider">
                    {group}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                    {keys.map((key) => {
                      const entry = attributeIconMap[key];
                      return (
                        <div
                          key={key}
                          className="flex min-w-0 items-start justify-between gap-3 border-b border-gray-700/50 py-1.5"
                        >
                          <AttributeLabel
                            iconKey={key as AttributeIconKey}
                            textClassName="text-gray-200 text-sm"
                          >
                            {entry.label}
                          </AttributeLabel>
                          <code className="max-w-[50%] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap pt-0.5 text-[11px] text-gray-500">
                            {key}
                          </code>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Code-Beispiel */}
        <div>
          <h3 className="text-sm font-semibold text-gray-400 mb-2 uppercase tracking-wider">
            Verwendung
          </h3>
          <pre className="bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm">
            <code className="text-gray-300">{`import AttributeLabel from '../components/AttributeLabel';

// Standard-Feldlabel mit Icon
<AttributeLabel iconKey="email">E-Mail</AttributeLabel>

// Als echtes Formular-Label (behält label/for-Zuordnung)
<AttributeLabel iconKey="phone" htmlFor="phone">Telefon</AttributeLabel>
<input id="phone" ... />

// Kompakt für mobile Karten
<AttributeLabel iconKey="address" size="sm">Anschrift</AttributeLabel>

// Positiv/aktiv hervorgehoben (Grün nur für aktive Zustände)
<AttributeLabel iconKey="active" tone="primary">Aktiv</AttributeLabel>

// Icon direkt aus der zentralen Map
import { getAttributeIcon } from '../utils/attributeIcons';
const Icon = getAttributeIcon('dog');`}</code>
          </pre>
        </div>
      </section>

      {/* Modal-System (ModalShell) */}
      <section className="bg-gray-800 rounded-lg p-6 space-y-4 border border-gray-700">
        <h2 className="text-xl font-bold text-white flex items-center">
          <span className="bg-primary-600 text-white px-2 py-1 rounded text-sm mr-2">NEU</span>
          Modal-System · ModalShell
        </h2>
        <p className="text-gray-400 text-sm">
          Ein gemeinsamer Rahmen (<code className="text-primary-300">ModalShell</code>) mit fünf
          Varianten. Einheitlicher Backdrop (schwarz 55 % + 2px Blur), 3px-Akzentlinie oben,
          Header 64px, sticky Footer, Fokusfalle, Body-Scroll-Lock und zentraler Z-Index-Stack.
          Mobil: <code className="text-primary-300">form/detail/choice</code> als Bottom-Sheet,
          <code className="text-primary-300"> confirm</code> zentriert,
          <code className="text-primary-300"> viewer</code> Vollbild.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" onClick={() => setDemoModal('confirm')}>confirm (danger)</Button>
          <Button variant="secondary" onClick={() => setDemoModal('form')}>form</Button>
          <Button variant="secondary" onClick={() => setDemoModal('detail')}>detail</Button>
          <Button variant="secondary" onClick={() => setDemoModal('choice')}>choice</Button>
          <Button variant="secondary" onClick={() => setDemoModal('viewer')}>viewer</Button>
        </div>

        {/* confirm (destruktiv) */}
        <ModalShell
          open={demoModal === 'confirm'}
          onClose={() => setDemoModal(null)}
          variant="confirm"
          size="sm"
          accent="danger"
          icon={TrashOutlineIcon}
          title="Eintrag löschen?"
          subtitle="Diese Aktion kann nicht rückgängig gemacht werden."
          footer={
            <ModalActions
              danger
              secondary={{ label: 'Abbrechen', onClick: () => setDemoModal(null) }}
              primary={{ label: 'Löschen', onClick: () => setDemoModal(null) }}
            />
          }
        >
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Soll dieser Beispiel-Eintrag wirklich gelöscht werden?
          </p>
        </ModalShell>

        {/* form */}
        <ModalShell
          open={demoModal === 'form'}
          onClose={() => setDemoModal(null)}
          variant="form"
          size="md"
          icon={UserIcon}
          title="Neues Mitglied"
          subtitle="Stammdaten erfassen"
          closeOnBackdrop={false}
          footer={
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: () => setDemoModal(null) }}
              primary={{ label: 'Speichern', onClick: () => setDemoModal(null) }}
            />
          }
        >
          <ModalSection title="Persönliche Daten" className="space-y-3">
            <input placeholder="Vorname" className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white" />
            <input placeholder="Nachname" className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white" />
          </ModalSection>
        </ModalShell>

        {/* detail */}
        <ModalShell
          open={demoModal === 'detail'}
          onClose={() => setDemoModal(null)}
          variant="detail"
          size="md"
          icon={UserIcon}
          title="Andrea Duwe"
          subtitle="Nr. 02346558"
          footer={
            <ModalActions
              secondary={{ label: 'Schließen', onClick: () => setDemoModal(null) }}
              primary={{ label: 'Bearbeiten', onClick: () => setDemoModal(null) }}
            />
          }
        >
          <ModalSection title="Persönliche Daten">
            <ModalRow label="Name">Andrea Duwe</ModalRow>
            <ModalRow label="Geburtsdatum">13.03.1995</ModalRow>
            <ModalRow label="E-Mail">andrea.duwe.sehr.lange.adresse@example.com</ModalRow>
          </ModalSection>
        </ModalShell>

        {/* choice */}
        <ModalShell
          open={demoModal === 'choice'}
          onClose={() => setDemoModal(null)}
          variant="choice"
          size="md"
          icon={DocumentTextIcon}
          title="Buchung zuordnen"
          subtitle="Treffer auswählen"
          footer={
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: () => setDemoModal(null) }}
              primary={{ label: 'Übernehmen', onClick: () => setDemoModal(null) }}
            />
          }
        >
          <div className="space-y-2">
            {['Rechnung Hoffmann', 'Beitrag 2026', 'Getränke Juli'].map((x) => (
              <div key={x} className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
                <span className="h-3 w-3 rounded-full border-2 border-primary-500" /> {x}
              </div>
            ))}
          </div>
        </ModalShell>

        {/* viewer */}
        <ModalShell
          open={demoModal === 'viewer'}
          onClose={() => setDemoModal(null)}
          variant="viewer"
          size="lg"
          title="Dokumentvorschau"
          subtitle="beispiel.pdf"
          footer={
            <ModalActions
              secondary={{ label: 'Download', onClick: () => setDemoModal(null) }}
              primary={{ label: 'Schließen', onClick: () => setDemoModal(null) }}
            />
          }
        >
          <div className="grid min-h-[300px] place-items-center text-gray-400 text-sm">
            Dokument- oder Bildfläche
          </div>
        </ModalShell>
      </section>

      {/* Usage Examples */}
      <section className="bg-gray-800 rounded-lg p-6 space-y-4 border border-gray-700">
        <h2 className="text-xl font-bold text-white">Code-Beispiele</h2>

        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-2">Button Verwendung</h3>
            <pre className="bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm">
              <code className="text-gray-300">{`// Primary Button
<Button variant="primary">Speichern</Button>

// Mit Icon
<Button variant="primary" icon={<PlusIcon />}>
  Hinzufügen
</Button>

// Mit Loading State
<Button variant="primary" loading={isLoading}>
  Speichern
</Button>

// Secondary / Tertiary
<Button variant="secondary">Abbrechen</Button>
<Button variant="tertiary">Optionen</Button>

// Feedback Buttons
<Button variant="success">Bestätigen</Button>
<Button variant="warning">Warnung</Button>
<Button variant="error">Löschen</Button>`}</code>
            </pre>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-2">Skeleton Loader Verwendung</h3>
            <pre className="bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm">
              <code className="text-gray-300">{`// Während Daten laden
{loading ? (
  <MemberSkeleton count={5} />
) : (
  <MemberList data={members} />
)}

// Stat Cards
{loading ? (
  <StatCardSkeleton count={4} />
) : (
  <StatsGrid stats={stats} />
)}

// Custom Skeleton
<SkeletonLoader
  variant="text"
  width="60%"
  height={20}
/>`}</code>
            </pre>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-400 mb-2">Farben Verwendung</h3>
            <pre className="bg-gray-900 p-4 rounded-lg overflow-x-auto text-sm">
              <code className="text-gray-300">{`// Success
<div className="bg-success-600 text-white">
  Erfolgreich!
</div>

// Warning
<div className="bg-warning-600 text-white">
  Achtung!
</div>

// Error
<div className="bg-error-600 text-white">
  Fehler!
</div>

// Info
<div className="bg-info-600 text-white">
  Information
</div>`}</code>
            </pre>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComponentsDemo;
